import type { DefLookup } from "./catalog";
import type { Cable, ComponentDefinition, SystemNode, Terminal } from "./types";

/** A monitor terminal at or under this rating measures voltage; it never
 *  carries load current (Victron BMV-712 / SmartShunt: B+ sense fused 1 A). */
export const SENSE_TERMINAL_MAX_AMP = 1;

/**
 * A voltage-sense lead: the catalog's `signal` terminals (a shunt's B+ /
 * AUX sense), or a monitor's 1 A bidirectional positive post, which is how a
 * custom monitor declares the same thing. Whatever hangs off it, a sense wire
 * carries milliamps — it is never a bus, and current is never summed through
 * it.
 */
export function isSenseTerminal(def: ComponentDefinition, t: Terminal): boolean {
  if (t.polarity === "signal") return true;
  return (
    def.category === "monitor" &&
    t.direction === "bidi" &&
    t.maxAmp > 0 &&
    t.maxAmp <= SENSE_TERMINAL_MAX_AMP &&
    (t.polarity === "positive" || t.polarity === "neutral")
  );
}

/** Either end of the cable lands on a sense terminal. */
export function isSenseCable(
  cable: Cable,
  nodesById: Record<string, SystemNode>,
  defLookup: DefLookup,
): boolean {
  for (const end of [
    { nodeId: cable.fromNodeId, termId: cable.fromTerminalId },
    { nodeId: cable.toNodeId, termId: cable.toTerminalId },
  ]) {
    const node = nodesById[end.nodeId];
    if (node?.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    const term = def?.terminals.find((t) => t.id === end.termId);
    if (def && term && isSenseTerminal(def, term)) return true;
  }
  return false;
}
